// Bilingual shared tools data — single source of truth for /<lang>/tools
// and for the site-wide search index.
//
// Each tool is declared ONCE. A tool may live in multiple categories via
// `categories: [...]` (e.g. NotebookLM lives in both Research and PDF;
// Kimi and DeepSeek live in both General Chat and Coding).
//
// Textual fields are either plain strings (shared across languages) or
// `{ar, en}` dicts read via `pickLang(field, lang)`.

/* -----------------------------------------------------------------------
 * Categories (ordered as they appear on the page)
 * -------------------------------------------------------------------- */
export const TOOL_CATEGORIES = [
  {
    id: "general-chat",
    label: { ar: "الدردشة والمساعدة العامة", en: "Chat and general AI" },
    blurb: {
      ar: "مساعدون ذكيون للمهام اليومية كالكتابة والتلخيص والشرح والإجابة عن الأسئلة.",
      en: "Everyday assistants for writing, summarising, explaining, and answering questions.",
    },
  },
  {
    id: "voice-english",
    label: { ar: "التحدث وممارسة الإنجليزية", en: "Voice & English speaking" },
    blurb: {
      ar: "أدوات للتدريب على المحادثة الصوتية وتحسين الطلاقة باللغة الإنجليزية.",
      en: "Tools for interactive voice practice and improving spoken English.",
    },
  },
  {
    id: "research",
    label: { ar: "البحث العلمي والتعلّم", en: "Research and learning" },
    blurb: {
      ar: "محركات ومساعدون للبحث العلمي مع روابط مرجعية ودعم قراءة الدراسات.",
      en: "Research engines and assistants with cited sources and literature support.",
    },
  },
  {
    id: "pdf",
    label: { ar: "قراءة PDF واستخلاص المعلومات", en: "PDF & information extraction" },
    blurb: {
      ar: "أدوات لفهم الملفات والدراسات واستخلاص الأفكار منها بسرعة.",
      en: "Tools for chatting with PDFs, understanding papers, and extracting key points.",
    },
  },
  {
    id: "presentations",
    label: { ar: "العروض والملخصات البصرية", en: "Presentations & visuals" },
    blurb: {
      ar: "أدوات لبناء عروض تقديمية وصفحات وملخصات بصرية بأسلوب حديث.",
      en: "Build modern presentations, visual pages, and explainer diagrams quickly.",
    },
  },
  {
    id: "coding-data",
    label: { ar: "البرمجة وتحليل البيانات", en: "Coding & data" },
    blurb: {
      ar: "بيئات ومساعدون للبرمجة وتحليل البيانات والتعلّم الآلي للمبتدئين والمتقدمين.",
      en: "Notebooks and AI assistants for programming, data analysis, and ML.",
    },
  },
  {
    id: "translation",
    label: { ar: "الترجمة وتحسين النص", en: "Translation & text" },
    blurb: {
      ar: "أدوات ترجمة عالية الجودة وفهم استخدام الكلمات في سياقها.",
      en: "High-quality translation and in-context word usage tools.",
    },
  },
  {
    id: "images",
    label: { ar: "الصور والتصميم", en: "Image & design" },
    blurb: {
      ar: "أدوات توليد صور وتصاميم بصرية انطلاقًا من وصف نصي.",
      en: "Generate images and visual designs from text prompts.",
    },
  },
  {
    id: "teaching",
    label: { ar: "التعليم والمعلّمين", en: "Teaching & educators" },
    blurb: {
      ar: "أدوات تُسرّع تحضير المواد التعليمية والأنشطة الصفية وتبسيط النصوص.",
      en: "Speed up lesson prep, classroom activities, and adapting materials.",
    },
  },
  {
    id: "vpn",
    label: { ar: "الخصوصية والوصول الآمن (VPN)", en: "Privacy & secure access (VPN)" },
    blurb: {
      ar: "خدمات VPN مجانية لوصول أكثر استقرارًا وحماية أقوى على الشبكات العامة.",
      en: "Free VPN services for more stable access and stronger public-network privacy.",
    },
    note: {
      ar: "استخدم أدوات VPN بمسؤولية ووفق القوانين والسياسات المحلية.",
      en: "Use VPN tools responsibly and according to local laws and policies.",
    },
  },
];

/* -----------------------------------------------------------------------
 * Tools
 * -------------------------------------------------------------------- */
export const TOOLS = [
  /* ---- 1. Chat and general AI ------------------------------------- */
  {
    slug: "chatgpt-free",
    name: "ChatGPT Free",
    categories: ["general-chat"],
    url: "https://openai.com/chatgpt/",
    desc: {
      ar: "مساعد ذكي مناسب للشرح والكتابة والتلخيص وتنظيم الأفكار والإجابة عن الأسئلة اليومية والدراسية.",
      en: "A versatile AI assistant for writing, summarising, explaining ideas, organising tasks, and answering everyday or study-related questions.",
    },
    audience: {
      ar: "الطلاب والمبتدئين وصنّاع المحتوى",
      en: "Students, beginners, and content creators",
    },
    whenToUse: {
      ar: "عندما تحتاج مساعدًا سريعًا لشرح فكرة أو كتابة مسودة أو تحسين نص.",
      en: "When you need a fast assistant for explaining a topic, drafting text, or improving writing.",
    },
  },
  {
    slug: "google-gemini",
    name: "Google Gemini",
    categories: ["general-chat"],
    url: "https://gemini.google.com/",
    desc: {
      ar: "مساعد من Google للفهم السريع والعصف الذهني والقراءة وصياغة الأفكار والنصوص.",
      en: "Google’s AI assistant for fast understanding, brainstorming, reading, planning, and generating useful text and ideas.",
    },
    audience: { ar: "الطلاب والمعلمين والباحثين", en: "Students, educators, and researchers" },
    whenToUse: {
      ar: "عندما تحتاج دعمًا مرنًا للفهم والتفسير وإنتاج المحتوى.",
      en: "When you need flexible support for interpretation, learning, and content generation.",
    },
  },
  {
    slug: "microsoft-copilot",
    name: "Microsoft Copilot",
    categories: ["general-chat"],
    url: "https://copilot.microsoft.com/",
    desc: {
      ar: "مساعد من Microsoft للأسئلة العامة والكتابة والتلخيص وبعض مهام الإنتاجية المرتبطة بالويب.",
      en: "Microsoft’s AI assistant for general questions, writing, summarising, and lightweight web productivity tasks.",
    },
    audience: { ar: "المستخدمين العامين والطلاب", en: "General users and students" },
    whenToUse: {
      ar: "عندما تريد مساعدًا مجانيًا بسيطًا ومباشرًا.",
      en: "When you want a simple and accessible free AI assistant.",
    },
  },
  {
    slug: "kimi",
    name: "Kimi",
    categories: ["general-chat", "coding-data"],
    url: "https://kimi.ai/",
    desc: {
      ar: "أداة قوية جدًا للتعامل مع السياق الطويل والملفات والتلخيصات والمهام المركّبة، بأداء مميز في الخطة المجانية.",
      en: "A very strong free tool for long-context tasks, files, summaries, and more complex multi-step use.",
    },
    audience: {
      ar: "الطلاب المتقدمين والباحثين ومن يتعامل مع نصوص طويلة",
      en: "Advanced students, researchers, and users working with long text",
    },
    whenToUse: {
      ar: "عندما تحتاج أداة تتحمّل سياقًا طويلًا وملفات ومهام تحليلية ممتدة.",
      en: "When you need a tool that handles long context, files, and extended reasoning more comfortably.",
    },
  },
  {
    slug: "deepseek",
    name: "DeepSeek",
    categories: ["general-chat", "coding-data"],
    url: "https://www.deepseek.com/",
    desc: {
      ar: "مساعد قوي في الكتابة والبرمجة والتحليل والاستخدام العملي الواسع.",
      en: "A strong free AI assistant for writing, coding, reasoning, and broader practical use.",
    },
    audience: { ar: "الطلاب والمبرمجين والمستخدمين المتقدمين", en: "Students, developers, and advanced users" },
    whenToUse: {
      ar: "عندما تريد بديلًا مجانيًا قويًا للتحليل والكتابة والدعم التقني.",
      en: "When you want a powerful free alternative for analysis, writing, and technical support.",
    },
  },

  /* ---- 2. Voice & English speaking -------------------------------- */
  {
    slug: "hume",
    name: "Hume",
    categories: ["voice-english"],
    url: "https://www.hume.ai/",
    recommended: true,
    desc: {
      ar: "أداة محادثة صوتية متقدمة نرشّحها كأفضل خيار في هذا القسم للتفاعل الصوتي والتدريب على التحدث ومحادثات أكثر طبيعية.",
      en: "An advanced voice AI tool that we recommend as the top option in this category for interactive speaking practice and natural voice conversation.",
    },
    audience: {
      ar: "تعلم المحادثة والتدريب الصوتي وتطوير الطلاقة",
      en: "Speaking practice, interactive voice learning, and improving fluency",
    },
    whenToUse: {
      ar: "عندما تريد تجربة صوتية أكثر تفاعلية من الدردشة النصية العادية.",
      en: "When you want a more dynamic spoken experience than standard text-first assistants.",
    },
  },
  {
    slug: "gliglish",
    name: "Gliglish",
    categories: ["voice-english"],
    url: "https://gliglish.com/",
    desc: {
      ar: "منصة عملية للتدرّب على المحادثة بالإنجليزية مع الذكاء الاصطناعي بطريقة مباشرة وبسيطة.",
      en: "A useful platform for practising spoken English with AI in a direct and practical way.",
    },
    audience: {
      ar: "المتعلمين الذين يريدون تحسين التحدث وسرعة الاستجابة",
      en: "Learners who want to improve speaking and response speed",
    },
    whenToUse: {
      ar: "عندما تحتاج ممارسة منتظمة للحديث بأسلوب بسيط.",
      en: "When you want regular speaking practice in a simple format.",
    },
  },
  {
    slug: "pi",
    name: "Pi",
    categories: ["voice-english"],
    url: "https://pi.ai/",
    desc: {
      ar: "مساعد محادثة ودود وهادئ يساعد على التدرّب على الحوار وبناء الثقة في التحدث.",
      en: "A calm and friendly conversational assistant that can help with relaxed speaking practice.",
    },
    audience: {
      ar: "المبتدئين ومن يفضّلون أسلوب محادثة هادئًا",
      en: "Beginners and learners who prefer a softer conversation style",
    },
    whenToUse: {
      ar: "عندما تريد بيئة حوار بسيطة لبناء الثقة في التحدث.",
      en: "When you want a gentle dialogue environment for building speaking confidence.",
    },
  },
  {
    slug: "character-ai",
    name: "Character.AI",
    categories: ["voice-english"],
    url: "https://character.ai/",
    desc: {
      ar: "منصة محادثة تفاعلية يمكن استخدامها للتدرّب على اللغة والحوار مع شخصيات مختلفة.",
      en: "An interactive conversation platform that can be used for dialogue-based language practice.",
    },
    audience: {
      ar: "محبّي الحوار التفاعلي والشخصيات المتنوعة",
      en: "Learners who enjoy role-based or character-style conversation",
    },
    whenToUse: {
      ar: "عندما تريد تدريبًا حواريًا أكثر تنوعًا ومتعة.",
      en: "When you want more playful or varied dialogue practice.",
    },
  },

  /* ---- 3. Research and learning ----------------------------------- */
  {
    slug: "perplexity",
    name: "Perplexity",
    categories: ["research"],
    url: "https://www.perplexity.ai/",
    desc: {
      ar: "محرك بحث وإجابات مدعوم بالذكاء الاصطناعي يعرض نتائج مباشرة مع مصادر وروابط مرجعية.",
      en: "An AI-powered answer and research engine that gives direct responses with linked sources.",
    },
    audience: { ar: "الباحثين والطلاب", en: "Researchers and students" },
    whenToUse: {
      ar: "لبداية البحث وجمع المصادر وبناء صورة أولية عن الموضوع.",
      en: "For starting research, gathering sources, and building an initial understanding of a topic.",
    },
  },
  {
    slug: "notebooklm",
    name: "NotebookLM",
    categories: ["research", "pdf"],
    url: "https://notebooklm.google.com/",
    desc: {
      ar: "أداة من Google لفهم الملفات والمراجع والمواقع وتحويلها إلى معرفة منظّمة وسهلة المراجعة.",
      en: "A Google tool for understanding files, references, and websites and turning them into organised knowledge.",
    },
    audience: { ar: "الباحثين والطلاب والمعلمين", en: "Researchers, students, and educators" },
    whenToUse: {
      ar: "عندما يكون لديك عدة ملفات PDF أو مراجع وتريد تلخيصها وربط أفكارها بكفاءة.",
      en: "When you have multiple PDFs or references and want to summarise and connect ideas efficiently.",
    },
  },
  {
    slug: "semantic-scholar",
    name: "Semantic Scholar",
    categories: ["research"],
    url: "https://www.semanticscholar.org/",
    desc: {
      ar: "محرك بحث أكاديمي ممتاز للوصول إلى الأوراق العلمية والمواضيع البحثية.",
      en: "An excellent academic search engine for discovering papers and research topics.",
    },
    audience: { ar: "الباحثين وطلاب الدراسات العليا", en: "Researchers and graduate students" },
    whenToUse: {
      ar: "عندما تريد الوصول المباشر إلى الأوراق الأكاديمية والمراجع البحثية.",
      en: "When you want direct access to academic papers and research references.",
    },
  },
  {
    slug: "consensus",
    name: "Consensus",
    categories: ["research"],
    url: "https://consensus.app/",
    desc: {
      ar: "أداة بحث علمي تعتمد على الدراسات والأوراق لتقديم إجابات مدعومة أكاديميًا.",
      en: "A research-focused tool that answers questions based on scientific papers and evidence.",
    },
    audience: { ar: "الباحثين والطلاب", en: "Researchers and students" },
    whenToUse: {
      ar: "عندما تريد إجابات علمية مبنية على الأدلة لا على التخمين.",
      en: "When you want academically grounded answers rather than generic summaries.",
    },
  },
  {
    slug: "elicit",
    name: "Elicit",
    categories: ["research"],
    url: "https://elicit.com/",
    desc: {
      ar: "أداة بحث تساعد في تلخيص الأوراق واستخراج المعلومات ودعم المراجعات العلمية.",
      en: "A research assistant tool that helps with paper summaries, information extraction, and literature support.",
    },
    audience: { ar: "الباحثين والمهتمين بالدراسات الأكاديمية", en: "Researchers and academic users" },
    whenToUse: {
      ar: "عندما تحتاج مساعدة عملية في قراءة وفرز الأدبيات العلمية.",
      en: "When you need practical help reviewing and organising academic literature.",
    },
  },

  /* ---- 4. PDF and information extraction -------------------------- */
  {
    slug: "chatpdf",
    name: "ChatPDF",
    categories: ["pdf"],
    url: "https://www.chatpdf.com/",
    desc: {
      ar: "أداة للدردشة مع ملفات PDF واستخراج الفكرة الأساسية منها بسرعة.",
      en: "A tool for chatting with PDF files and extracting key ideas quickly.",
    },
    audience: { ar: "الطلاب والباحثين", en: "Students and researchers" },
    whenToUse: {
      ar: "عندما تريد طرح أسئلة مباشرة على ملف PDF طويل.",
      en: "When you want to ask direct questions about a long PDF.",
    },
  },
  {
    slug: "openread",
    name: "OpenRead",
    categories: ["pdf"],
    url: "https://www.openread.academy/",
    desc: {
      ar: "منصة مفيدة للتعامل مع الأوراق العلمية واستخلاص المعلومات المهمة منها.",
      en: "A useful platform for reading scientific papers and extracting important information from them.",
    },
    audience: { ar: "الباحثين والقراء الأكاديميين", en: "Researchers and academic readers" },
    whenToUse: {
      ar: "عندما تريد بيئة أكثر تخصصًا لقراءة المواد العلمية.",
      en: "When you want a more specialised research reading environment.",
    },
  },
  {
    slug: "explainpaper",
    name: "Explainpaper",
    categories: ["pdf"],
    url: "https://www.explainpaper.com/",
    desc: {
      ar: "أداة تبسّط المقاطع الصعبة من الأوراق العلمية وتشرحها بلغة أوضح.",
      en: "A helpful tool that explains difficult passages in research papers in simpler language.",
    },
    audience: { ar: "الطلاب والباحثين", en: "Students and researchers" },
    whenToUse: {
      ar: "عندما تواجه فقرة علمية معقّدة وتحتاج شرحًا مباشرًا.",
      en: "When you need direct clarification for complex scientific text.",
    },
  },
  {
    slug: "pdfgear",
    name: "PDFgear",
    categories: ["pdf"],
    url: "https://www.pdfgear.com/",
    desc: {
      ar: "أداة عملية للتعامل مع ملفات PDF تدعم القراءة والتعديل وبعض المزايا الذكية.",
      en: "A practical PDF tool with useful reading, editing, and lightweight AI-related features.",
    },
    audience: { ar: "المستخدمين العامين والطلاب", en: "General users and students" },
    whenToUse: {
      ar: "عندما تحتاج أداة PDF بسيطة للاستخدام اليومي.",
      en: "When you need an everyday PDF tool that is simple and useful.",
    },
  },

  /* ---- 5. Presentations & visuals --------------------------------- */
  {
    slug: "gamma",
    name: "Gamma",
    categories: ["presentations"],
    url: "https://gamma.app/",
    desc: {
      ar: "أداة حديثة لصناعة عروض تقديمية وصفحات محتوى بسرعة وبأناقة.",
      en: "A modern AI-powered tool for creating polished presentations and visual pages quickly.",
    },
    audience: { ar: "الطلاب والمدربين وصنّاع المحتوى", en: "Students, trainers, and content creators" },
    whenToUse: {
      ar: "عند تحضير عرض أو ملخص بصري أو صفحة تعريفية لفكرة أو مشروع.",
      en: "When preparing a presentation, visual summary, proposal page, or project overview.",
    },
  },
  {
    slug: "napkin",
    name: "Napkin",
    categories: ["presentations"],
    url: "https://www.napkin.ai/",
    desc: {
      ar: "أداة لتحويل الأفكار والنصوص إلى تمثيلات بصرية ومخططات مبسّطة.",
      en: "A useful tool for turning ideas and text into clean visual diagrams and explanation graphics.",
    },
    audience: { ar: "العروض والتوضيح وصناعة المحتوى البصري", en: "Presentations, explanation, and visual communication" },
    whenToUse: {
      ar: "عندما تريد تحويل الأفكار إلى بنية بصرية سهلة الفهم.",
      en: "When you want to convert ideas into easy visual structure.",
    },
  },
  {
    slug: "whimsical",
    name: "Whimsical",
    categories: ["presentations"],
    url: "https://whimsical.com/",
    desc: {
      ar: "منصة ممتازة للمخططات الذهنية وخرائط التدفق وتنظيم الأفكار بصريًا.",
      en: "An excellent platform for mind maps, flowcharts, and visual idea organisation.",
    },
    audience: { ar: "الطلاب والفرق وأصحاب المشاريع", en: "Students, teams, and project builders" },
    whenToUse: {
      ar: "عندما تحتاج تنظيم الأفكار أو شرح عملية بصريًا.",
      en: "When you need to organise thinking or explain a process visually.",
    },
  },

  /* ---- 6. Coding & data ------------------------------------------- */
  {
    slug: "google-colab",
    name: "Google Colab",
    categories: ["coding-data"],
    url: "https://colab.research.google.com/",
    desc: {
      ar: "بيئة سحابية مجانية لتشغيل دفاتر Python دون إعدادات معقّدة.",
      en: "A free cloud-based Python notebook environment for coding experiments and data analysis without complex setup.",
    },
    audience: { ar: "طلاب البرمجة ومتعلّمي البيانات", en: "Programming students, data learners, and practical experimentation" },
    whenToUse: {
      ar: "عندما تريد كتابة كود أو تجربة فكرة أو تحليل بيانات بسرعة.",
      en: "When you want to write code, test ideas, or analyse data quickly.",
    },
  },
  {
    slug: "orange-data-mining",
    name: "Orange Data Mining",
    categories: ["coding-data"],
    url: "https://orangedatamining.com/",
    desc: {
      ar: "أداة مفتوحة المصدر للتحليل البصري للبيانات وتعلّم الآلة، مناسبة جدًا للمبتدئين.",
      en: "An open-source visual data analysis and machine learning tool that is especially friendly for beginners.",
    },
    audience: {
      ar: "الطلاب والباحثين ومبتدئي تحليل البيانات",
      en: "Students, researchers, and first-time data learners",
    },
    whenToUse: {
      ar: "عندما تريد فهم البيانات أو تجربة نماذج تعلّم آلة بطريقة بصرية وبأقل كود.",
      en: "When you want to explore data or try machine learning workflows with less code.",
    },
  },

  /* ---- 7. Translation & text -------------------------------------- */
  {
    slug: "deepl",
    name: "DeepL",
    categories: ["translation"],
    url: "https://www.deepl.com/",
    desc: {
      ar: "أداة ترجمة معروفة بجودتها العالية في كثير من الاستخدامات النصية.",
      en: "A well-known translation tool with strong quality in many text-based use cases.",
    },
    audience: { ar: "الطلاب والكتّاب والمترجمين", en: "Students, writers, and translators" },
    whenToUse: {
      ar: "عندما تحتاج ترجمة بجودة جيدة وسياق أوضح.",
      en: "When you need better translation quality and clearer text flow.",
    },
  },
  {
    slug: "reverso",
    name: "Reverso",
    categories: ["translation"],
    url: "https://www.reverso.net/",
    desc: {
      ar: "أداة مفيدة للترجمة والسياق والأمثلة اللغوية الواقعية.",
      en: "A useful tool for translation, usage examples, and understanding words in context.",
    },
    audience: { ar: "المتعلمين والمترجمين", en: "Learners and translators" },
    whenToUse: {
      ar: "عندما تريد فهم استخدام الكلمات والعبارات داخل سياقها الحقيقي.",
      en: "When you want to understand how words and expressions are used in real context.",
    },
  },
  {
    slug: "immersive-translate",
    name: "Immersive Translate",
    categories: ["translation"],
    url: "https://immersivetranslate.com/",
    desc: {
      ar: "أداة عملية لترجمة صفحات الويب وقراءة المحتوى الأجنبي بمرونة.",
      en: "A practical tool for translating webpages and reading foreign-language content more comfortably.",
    },
    audience: {
      ar: "المتعلمين والقرّاء المتكررين للمحتوى الأجنبي",
      en: "Learners and frequent readers of international content",
    },
    whenToUse: {
      ar: "عندما تحتاج وصولًا أسهل إلى محتوى الويب بلغات أخرى.",
      en: "When you need easier access to foreign-language web content.",
    },
  },

  /* ---- 8. Image & design ------------------------------------------ */
  {
    slug: "ideogram",
    name: "Ideogram",
    categories: ["images"],
    url: "https://ideogram.ai/",
    desc: {
      ar: "أداة قوية لتوليد الصور والتعامل مع النص داخل الصور بطريقة مرنة.",
      en: "A strong tool for generating images and handling text-inside-image use cases.",
    },
    audience: { ar: "المصممين وصنّاع المحتوى", en: "Designers and content creators" },
    whenToUse: {
      ar: "عندما تريد إنتاج صور أو تصاميم مفاهيمية بسرعة.",
      en: "When you want to create concept visuals or image-based content quickly.",
    },
  },
  {
    slug: "leonardo",
    name: "Leonardo",
    categories: ["images"],
    url: "https://leonardo.ai/",
    desc: {
      ar: "أداة متقدمة لتوليد الصور والمشاهد البصرية والأفكار الإبداعية.",
      en: "An advanced image generation tool for visual ideas, creative scenes, and design experimentation.",
    },
    audience: { ar: "المصممين وصنّاع المحتوى الإبداعي", en: "Designers and creative content makers" },
    whenToUse: {
      ar: "عندما تحتاج خيارات توليد صور أكثر مرونة وتحكّمًا.",
      en: "When you want more flexible image generation options.",
    },
  },
  {
    slug: "playground",
    name: "Playground",
    categories: ["images"],
    url: "https://playground.com/",
    desc: {
      ar: "أداة عملية لتوليد الصور وتحريرها بسرعة لاختبار الأفكار.",
      en: "A practical image generation tool for fast concept creation and editing.",
    },
    audience: { ar: "المبتدئين وصنّاع المحتوى", en: "Beginners and content creators" },
    whenToUse: {
      ar: "عندما تريد تجربة أفكار بصرية بسرعة ودون تعقيد.",
      en: "When you want to test visual ideas quickly and simply.",
    },
  },
  {
    slug: "image-fx",
    name: "ImageFX",
    categories: ["images"],
    url: "https://labs.google/fx/tools/image-fx",
    desc: {
      ar: "أداة من Google لتوليد الصور بالذكاء الاصطناعي بأسلوب مباشر وبسيط.",
      en: "A Google AI image tool that makes prompt-based image generation simple and approachable.",
    },
    audience: {
      ar: "المستخدمين الذين يريدون تجربة سهلة ومباشرة للصور",
      en: "Users who want an easy and direct visual generation experience",
    },
    whenToUse: {
      ar: "عندما تريد توليد صور من وصف نصي بطريقة مبسّطة.",
      en: "When you want a straightforward way to generate images from text.",
    },
  },

  /* ---- 9. Teaching & educators ------------------------------------ */
  {
    slug: "magicschool",
    name: "MagicSchool",
    categories: ["teaching"],
    url: "https://www.magicschool.ai/",
    desc: {
      ar: "منصة تساعد المعلمين على توليد أنشطة وأفكار تعليمية ومحتوى صفي بسرعة.",
      en: "A platform that helps teachers generate educational activities, ideas, and classroom content more quickly.",
    },
    audience: { ar: "المعلمين والمدربين", en: "Teachers and trainers" },
    whenToUse: {
      ar: "عندما تريد تسريع تحضير الدروس والمواد التعليمية.",
      en: "When you want to speed up lesson preparation and classroom material generation.",
    },
  },
  {
    slug: "diffit",
    name: "Diffit",
    categories: ["teaching"],
    url: "https://web.diffit.me/",
    desc: {
      ar: "أداة تساعد على تكييف المحتوى التعليمي وتبسيطه لمستويات طلاب مختلفة.",
      en: "A tool that helps adapt and simplify educational content for different student levels.",
    },
    audience: { ar: "المعلمين", en: "Teachers" },
    whenToUse: {
      ar: "عندما تريد تبسيط نصوص أو إعداد مواد تعليمية ميسّرة.",
      en: "When you want to adjust reading materials or create more accessible learning content.",
    },
  },
  {
    slug: "conker",
    name: "Conker",
    categories: ["teaching"],
    url: "https://www.conker.ai/",
    desc: {
      ar: "أداة تساعد على إنشاء اختبارات وأسئلة تعليمية بسرعة.",
      en: "A tool that helps generate quizzes and question-based educational activities more quickly.",
    },
    audience: { ar: "المعلمين والمدربين", en: "Teachers and trainers" },
    whenToUse: {
      ar: "عندما تحتاج إعداد أنشطة تقييمية بشكل أسرع.",
      en: "When you need faster assessment and quiz creation.",
    },
  },

  /* ---- 10. Privacy & VPN ------------------------------------------ */
  {
    slug: "proton-vpn-free",
    name: "Proton VPN Free",
    categories: ["vpn"],
    url: "https://protonvpn.com/free-vpn",
    desc: {
      ar: "خدمة VPN مجانية تركّز على الخصوصية وتوفّر خطة مجانية عملية لكثير من المستخدمين.",
      en: "A privacy-focused VPN service with a practical free plan suitable for many users.",
    },
    audience: {
      ar: "من يحتاج وصولًا أكثر استقرارًا أو خصوصية أفضل أثناء التصفح",
      en: "Users who need more stable access or stronger browsing privacy",
    },
    whenToUse: {
      ar: "عند الحاجة لوصول آمن إلى بعض الخدمات أو حماية أقوى على الشبكات العامة.",
      en: "When you need safer access to some services or stronger protection on public networks.",
    },
  },
  {
    slug: "windscribe-free",
    name: "Windscribe Free",
    categories: ["vpn"],
    url: "https://windscribe.com/features/use-for-free",
    desc: {
      ar: "خدمة VPN مع خطة مجانية عملية، مناسبة كخيار إضافي إلى جانب Proton VPN.",
      en: "A VPN service with a practical free plan that works as a useful second option next to Proton VPN.",
    },
    audience: {
      ar: "من يبحث عن بديل VPN مجاني وبسيط",
      en: "Users looking for a simple free alternative",
    },
    whenToUse: {
      ar: "كخيار ثانٍ عند الحاجة إلى VPN مجاني بواجهة سهلة.",
      en: "As a secondary VPN choice when a free and easy-to-use service is needed.",
    },
  },
];

/* -----------------------------------------------------------------------
 * "Start here" quick-picks — deliberately kept to 2 strong starting points
 * (one universal assistant + one research / files tool). Each item shows
 * a single recommended tool in a light, text-led format on the Tools page.
 * -------------------------------------------------------------------- */
export const QUICK_PICKS = [
  {
    id: "general",
    need: { ar: "مساعد عام للذكاء الاصطناعي", en: "A general AI assistant" },
    tools: ["chatgpt-free"],
  },
  {
    id: "files-refs",
    need: { ar: "فهم الملفات والمراجع", en: "Understanding files and references" },
    tools: ["notebooklm"],
  },
];

/* -----------------------------------------------------------------------
 * Helpers
 * -------------------------------------------------------------------- */
export const getToolBySlug = (slug) => TOOLS.find((t) => t.slug === slug) || null;
export const getCategory = (id) => TOOL_CATEGORIES.find((c) => c.id === id) || null;

/** Return all tools that belong to the given category id. */
export const getToolsByCategory = (categoryId) =>
  TOOLS.filter((t) => (t.categories || []).includes(categoryId));

/** Primary/first-declared category for a tool (used for display chip). */
export const primaryCategoryOf = (tool) =>
  getCategory((tool.categories && tool.categories[0]) || null);
